import { useSendNotification } from "@app/hooks/useNotification";
import { clientFetch } from "@app/lib/egress/client";
import { parseMatcherExpression } from "@app/lib/matcher";
import {
  emptyArray,
  getErrorFromResponse,
  useFetcher,
  useSWRWithDefaults,
} from "@app/lib/swr/swr";
import type { GetTriggerEstimationResponseBody } from "@app/lib/triggers/trigger_usage_estimation";
import type {
  GetTriggersResponseBody,
  GetUserTriggersResponseBody,
  PatchTriggersRequestBody,
  PostTextAsCronRuleRequestBody,
  PostTextAsCronRuleResponseBody,
  PostTriggersRequestBody,
} from "@app/types/api/assistant/configuration/triggers";
import type {
  PostWebhookFilterGeneratorRequestBody,
  PostWebhookFilterGeneratorResponseBody,
} from "@app/types/api/assistant/configuration/triggers/webhook_filter_generator";
import type { ScheduleConfig } from "@app/types/assistant/triggers";
import { Err, Ok } from "@app/types/shared/result";
import { normalizeError } from "@app/types/shared/utils/error_utils";
import type { WebhookProvider } from "@app/types/triggers/webhooks";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { Fetcher } from "swr";

export function useAgentTriggers({
  workspaceId,
  agentConfigurationId,
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const triggersFetcher: Fetcher<GetTriggersResponseBody> = fetcher;

  const { data, error, mutate, isValidating } = useSWRWithDefaults(
    agentConfigurationId
      ? `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/triggers`
      : null,
    triggersFetcher,
    { disabled }
  );

  return {
    triggers: data?.triggers ?? emptyArray(),
    isTriggersLoading: !!agentConfigurationId && !error && !data && !disabled,
    isTriggersError: error,
    isTriggersValidating: isValidating,
    mutateTriggers: mutate,
  };
}

export function useUserTriggers({
  workspaceId,
  disabled,
}: {
  workspaceId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const userTriggersFetcher: Fetcher<GetUserTriggersResponseBody> = fetcher;

  const { data, error, mutate, isValidating } = useSWRWithDefaults(
    `/api/w/${workspaceId}/me/triggers`,
    userTriggersFetcher,
    { disabled }
  );

  return {
    triggers: data?.triggers ?? emptyArray(),
    isTriggersLoading: !error && !data && !disabled,
    isTriggersError: error,
    isTriggersValidating: isValidating,
    mutateTriggers: mutate,
  };
}

export function useDeleteTrigger({
  workspaceId,
  agentConfigurationId,
}: {
  workspaceId: string;
  agentConfigurationId: string;
}) {
  const { mutateTriggers } = useAgentTriggers({
    workspaceId,
    agentConfigurationId,
    disabled: true,
  });

  const deleteTrigger = useCallback(
    async (triggerId: string): Promise<boolean> => {
      try {
        const response = await clientFetch(
          `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/triggers`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ triggerIds: [triggerId] }),
          }
        );

        if (response.ok) {
          void mutateTriggers();
          return true;
        } else {
          return false;
        }
      } catch {
        return false;
      }
    },
    [workspaceId, agentConfigurationId, mutateTriggers]
  );

  return deleteTrigger;
}

export function useCreateTrigger({
  workspaceId,
  agentConfigurationId,
}: {
  workspaceId: string;
  agentConfigurationId: string;
}) {
  const { t } = useTranslation();
  const sendNotification = useSendNotification();
  const { mutateTriggers } = useAgentTriggers({
    workspaceId,
    agentConfigurationId,
    disabled: true,
  });

  const createTrigger = useCallback(
    async (
      triggerData: PostTriggersRequestBody["triggers"][number]
    ): Promise<boolean> => {
      try {
        const response = await clientFetch(
          `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/triggers`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ triggers: [triggerData] }),
          }
        );

        if (response.ok) {
          sendNotification({
            type: "success",
            title: t("toasts.agents.triggerCreatedTitle"),
            description: t("toasts.agents.triggerCreatedDesc", {
              name: triggerData.name,
            }),
          });
          void mutateTriggers();
          return true;
        } else {
          const errorData = await getErrorFromResponse(response);
          sendNotification({
            type: "error",
            title: t("toasts.agents.triggerCreateFailedTitle"),
            description: t("toasts.agents.triggerErrorDesc", {
              message: errorData.message,
            }),
          });
          return false;
        }
      } catch {
        sendNotification({
          type: "error",
          title: t("toasts.agents.triggerCreateFailedTitle"),
          description: t("toasts.agents.unexpectedErrorDesc"),
        });
        return false;
      }
    },
    [workspaceId, agentConfigurationId, sendNotification, mutateTriggers, t]
  );

  return createTrigger;
}

export function useUpdateTrigger({
  workspaceId,
  agentConfigurationId,
}: {
  workspaceId: string;
  agentConfigurationId: string;
}) {
  const { t } = useTranslation();
  const sendNotification = useSendNotification();
  const { mutateTriggers } = useAgentTriggers({
    workspaceId,
    agentConfigurationId,
    disabled: true,
  });

  const updateTrigger = useCallback(
    async (
      triggerData: PatchTriggersRequestBody["triggers"][number]
    ): Promise<boolean> => {
      try {
        const response = await clientFetch(
          `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/triggers`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ triggers: [triggerData] }),
          }
        );

        if (response.ok) {
          sendNotification({
            type: "success",
            title: t("toasts.agents.triggerUpdatedTitle"),
            description: t("toasts.agents.triggerUpdatedDesc", {
              name: triggerData.name,
            }),
          });
          void mutateTriggers();
          return true;
        } else {
          const errorData = await getErrorFromResponse(response);
          sendNotification({
            type: "error",
            title: t("toasts.agents.triggerUpdateFailedTitle"),
            description: t("toasts.agents.triggerErrorDesc", {
              message: errorData.message,
            }),
          });
          return false;
        }
      } catch {
        sendNotification({
          type: "error",
          title: t("toasts.agents.triggerUpdateFailedTitle"),
          description: t("toasts.agents.unexpectedErrorDesc"),
        });
        return false;
      }
    },
    [workspaceId, agentConfigurationId, sendNotification, mutateTriggers, t]
  );

  return updateTrigger;
}

function responseToScheduleConfig(
  r: PostTextAsCronRuleResponseBody
): ScheduleConfig {
  if (r.type === "interval") {
    return {
      type: "interval",
      intervalDays: r.intervalDays,
      dayOfWeek: r.dayOfWeek,
      hour: r.hour,
      minute: r.minute,
      timezone: r.timezone,
    };
  }
  return { type: "cron", cron: r.cronRule, timezone: r.timezone };
}

export function useTextAsCronRule({
  workspace,
}: {
  workspace: LightWorkspaceType;
}) {
  const { fetcher } = useFetcher();
  const textAsCronRule = useCallback(
    async (naturalDescription: string, signal?: AbortSignal) => {
      let r: PostTextAsCronRuleResponseBody;
      try {
        r = await fetcher(
          `/api/w/${workspace.sId}/assistant/agent_configurations/text_as_cron_rule`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              naturalDescription,
              defaultTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            } satisfies PostTextAsCronRuleRequestBody),
            signal,
          }
        );
      } catch (e: unknown) {
        return new Err(normalizeError(e));
      }

      return new Ok(responseToScheduleConfig(r));
    },
    [workspace, fetcher]
  );

  return textAsCronRule;
}

export function useWebhookFilterGenerator({
  workspace,
}: {
  workspace: LightWorkspaceType;
}) {
  const { fetcher } = useFetcher();
  const generateFilter = useCallback(
    async ({
      naturalDescription,
      event,
      provider,
      signal,
    }: {
      naturalDescription: string;
      event: string;
      provider: WebhookProvider;
      signal?: AbortSignal;
    }): Promise<{ filter: string }> => {
      const r: PostWebhookFilterGeneratorResponseBody = await fetcher(
        `/api/w/${workspace.sId}/assistant/agent_configurations/webhook_filter_generator`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            naturalDescription,
            event,
            provider,
          } satisfies PostWebhookFilterGeneratorRequestBody),
          signal,
        }
      );

      const parseResult = parseMatcherExpression(r.filter);
      if (parseResult.isErr()) {
        throw new Error(parseResult.error.message);
      }

      return { filter: r.filter };
    },
    [workspace, fetcher]
  );

  return generateFilter;
}

export function useTriggerEstimation({
  workspaceId,
  webhookSourceId,
  filter,
  selectedEvent,
}: {
  workspaceId: string;
  webhookSourceId?: string | null;
  filter?: string | null;
  selectedEvent?: string | null;
}) {
  const { fetcher } = useFetcher();
  const key = webhookSourceId
    ? `/api/w/${workspaceId}/webhook_sources/${webhookSourceId}/trigger-estimation`
    : null;

  const triggerEstimationFetcher: (
    arg: string
  ) => Promise<GetTriggerEstimationResponseBody> = (baseUrl) => {
    const params = new URLSearchParams();
    if (filter && filter.trim()) {
      params.append("filter", filter);
    }
    if (selectedEvent) {
      params.append("event", selectedEvent);
    }
    const queryString = params.toString();
    const url = `${baseUrl}${queryString ? `?${queryString}` : ""}`;
    return fetcher(url);
  };

  const { data, error, isValidating, mutate } = useSWRWithDefaults(
    key,
    triggerEstimationFetcher,
    {
      revalidateOnMount: false,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  return {
    estimation: data ?? null,
    isEstimationLoading: !!webhookSourceId && !error && !data,
    isEstimationError: error,
    isEstimationValidating: isValidating,
    mutateEstimation: mutate,
  };
}
